import { SHORTHAND_STYLE_PROPS } from '../../../constants/props';
import { config } from '../../../system/config';
import { BREAKPOINTS_ORDER } from '../../../system/styles/breakpoints';
import {
  ResponsiveStyle,
  UinixResponsiveStyle,
} from '../../../types/ResponsiveStyle';
import { SharedBoxProps } from '../../../types/SharedBoxProps';

const RESPONSIVE_CSS_PROPERTIES = new Set(config.responsiveCssProperties);
const SHORTHAND_STYLE_KEYS = new Set(Object.keys(SHORTHAND_STYLE_PROPS));

const filterCSSProperties = (prop: string) =>
  RESPONSIVE_CSS_PROPERTIES.has(prop) || SHORTHAND_STYLE_KEYS.has(prop);

/**
 * Transform a responsive object value into an array of values for uinix-ui.
 * fela is used for responsive breakpoints under the hood, and it expects values to be undefined within the array if you want to skip it for a specific breakpoint.
 * @link https://fela.js.org/docs/latest/recipes/responsive-components#skipping-values
 *
 * @param value Responsive object value
 * @returns
 */
export const transformResponsiveStyleValue = (
  value: ResponsiveStyle<number | string>,
): UinixResponsiveStyle => {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return BREAKPOINTS_ORDER.map((breakpoint) => value[breakpoint]);
  }

  return value;
};

/**
 * Takes a set of props, filters out styling related props, and either passes the value through or transforms it as necessary.
 * @param props all Box props
 * @returns
 */
const transformResponsiveProps = (props: SharedBoxProps) => {
  const stylingPropsKeys = Object.keys(props).filter(filterCSSProperties);
  return stylingPropsKeys.reduce(
    (acc, key) => {
      const value = props[key];
      acc[key] = transformResponsiveStyleValue(value);
      return acc;
    },
    {} as Record<string, any>,
  );
};

/**
 * @param props all Box props
 * @returns all Box props - with any Responsive objects values transformed into arrays for uinix-ui
 */
export const transformProps = (props: SharedBoxProps) => {
  const responsiveProps = transformResponsiveProps(props);

  return {
    ...props,
    ...responsiveProps,
  };
};
